import { ChatMsgStatus, IChatMessage } from '@app-types/websocket/chat.types';

// The server's decision on a message held by the toxic check, mirrored from the backend's
// ToxicVerdict. 0 = fine, 1-9 = a rewrite is suggested but the message may be sent, 10+ = blocked.
// A number within a range is the reason; an unknown number is handled by its range.
// Only the verdict decides whether a held message may be sent, never the toxic level.
export const TOXIC_VERDICT = {
  FINE: 0,
  REWRITE_SUGGESTED: 1,
  BLOCKED: 10,
};

// The server gives up on a check after 15 seconds and then sends the message itself, so a
// message held this long without a verdict was never checked.
export const UNCHECKED_AFTER_MS = 20000;

// The rewrite the AI answers with when it has no suggestion, by app language.
export const NO_SUGGESTION_REWRITE = {
  SE: 'Jag har inget förslag på hur detta kan skrivas om.',
  EN: "I don't have a suggestion for how to rewrite this.",
};
// The AI words that answer itself, in the language of the message, so only its opening is fixed.
// It is a single clause: a longer text that starts the same way is a real rewrite.
const NO_SUGGESTION_PATTERNS = [
  /^jag har (tyvärr )?(inget|inga) förslag på hur [^,.!?]*$/,
  /^i (don't|do not) have (a|any) suggestions? (for|on) how [^,.!?]*$/,
  /^i have no suggestions? (for|on) how [^,.!?]*$/,
];

// A rewrite is suggested or the message is blocked.
export function isToxicFlagged(verdict: number | undefined): boolean {
  return verdict !== undefined && verdict > TOXIC_VERDICT.FINE;
}

export function isToxicBlocked(verdict: number | undefined): boolean {
  return verdict !== undefined && verdict >= TOXIC_VERDICT.BLOCKED;
}

// The sender has to act on a held message the check flagged, and on one no check is coming
// for. An unchecked message is handled like a flagged one that may be sent.
export function awaitsSenderAction(msg: IChatMessage, heldForMs: number): boolean {
  return msg.status === ChatMsgStatus.AWAIT_RESULT
    && (isToxicFlagged(msg.toxicVerdict) || heldForMs >= UNCHECKED_AFTER_MS);
}

export function hasRewrite(msg: IChatMessage): boolean {
  return typeof msg.rewrite === 'string' && msg.rewrite.trim().length > 0;
}

// The server only asks the AI for a rewrite of messages its check flagged.
export function isRewritePending(msg: IChatMessage): boolean {
  return isToxicFlagged(msg.toxicVerdict) && !hasRewrite(msg);
}

// The AI may answer that it has no suggestion: that answer is shown but cannot be used.
export function isRewriteUsable(msg: IChatMessage): boolean {
  if (!hasRewrite(msg)) {
    return false;
  }
  const rewrite = comparable(msg.rewrite as string);
  return !NO_SUGGESTION_PATTERNS.some((pattern) => pattern.test(rewrite));
}

function comparable(text: string): string {
  return text.trim().replace(/[‘’]/g, "'").replace(/\.$/, '').toLowerCase();
}



// WEBPACK FOOTER //
// ./src/services/chat/toxicVerdict.ts